import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Checkbox } from "@amzn/stencil-react-components/checkbox";
import { WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { Link } from "@amzn/stencil-react-components/link";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { IItemVersion } from "@/common/types/item.types";
import { useCheckoutForm } from "@/pages/checkout/hooks/use-checkout-form";
import { useSubmitOrder } from "@/pages/checkout/hooks/use-submit-order";
import { GetManagerApprovalCard } from "@/pages/checkout/components/GetManagerApprovalCard/get-manager-approval-card";
import { ChangeShippingDetailsCard } from "@/pages/checkout/components/ChangeShippingDetailsCard/change-shipping-details-card";
import { CancelOrderModal } from "@/pages/checkout/components/CancelOrderModal/cancel-order-modal";
import { Paths } from "@/common/paths/paths";
import React from "react";
import { useHistory } from "react-router-dom";

const STRING_PATH = "checkout.formPanel";
const hardwareOwnershipPolicyLink = "https://policy.a2z.com/docs/37992/publication";
const itServicesHardwarePolicyLink = "https://policy.a2z.com/docs/44534/publication";

export interface CheckoutFormPanelProps {
  selectedVersion: IItemVersion;
}

export const CheckoutFormPanel: React.FC<CheckoutFormPanelProps> = ({ selectedVersion }) => {
  const history = useHistory();
  const { form, updateField, isFilled } = useCheckoutForm();
  const { submitOrder, isSubmitting, submitError } = useSubmitOrder(selectedVersion);

  const onExitPage = (closeModal: () => void) => {
    history.push(Paths.search.url);
    closeModal();
  };

  return (
    <Col width="100%" data-testid="checkout-form-panel">
      <Container paddingHorizontal={80} paddingTop={32} paddingBottom={32} isFullWidth={true}>
        <Col gridGap="S400" width="100%">
          <Text fontSize="T500" fontWeight="bold" color="neutral90">
            {getString(`${STRING_PATH}.checkout`)}
          </Text>

          <MessageBanner data-testid="checkout-banner" isDismissible={true}>
            {getString(`${STRING_PATH}.banner`)}
          </MessageBanner>

          <Text fontSize="T200" color="neutral90">
            {getString(`${STRING_PATH}.requiredField`)}
          </Text>

          <GetManagerApprovalCard />
          <ChangeShippingDetailsCard />

          {/* Policy confirmation */}
          <Row gridGap="S200">
            <Checkbox
              data-testid="policy-checkbox"
              checked={form.policyConfirmationCheckbox}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                updateField("policyConfirmationCheckbox", event.target.checked)
              }
            />
            <Text fontSize="T200" color="neutral90">
              {getString(`${STRING_PATH}.iHaveRead`)}
              <Link href={hardwareOwnershipPolicyLink} target="_blank">
                {getString(`${STRING_PATH}.hardwareOwnershipPolicy`)}
              </Link>
              {getString(`${STRING_PATH}.and`)}
              <Link href={itServicesHardwarePolicyLink} target="_blank">
                {getString(`${STRING_PATH}.itServicesHardwarePolicy`)}
              </Link>
              . *
            </Text>
          </Row>

          {submitError && (
            <MessageBanner data-testid="checkout-error-banner" type={MessageBannerType.Error}>
              {getString(`${STRING_PATH}.submitError`)}
            </MessageBanner>
          )}

          {/* Buttons */}
          <Row gridGap="S300" display="flex" justifyContent="flex-end">
            <WithModal
              renderModal={({ close }) => <CancelOrderModal close={close} onConfirm={onExitPage} />}
              isScrollable={false}
            >
              {({ open }) => (
                <Button data-testid="cancel-order-button" variant={ButtonVariant.Tertiary} onClick={open}>
                  {getString(`${STRING_PATH}.cancel`)}
                </Button>
              )}
            </WithModal>
            <Button
              data-testid="submit-order-button"
              disabled={!isFilled || isSubmitting}
              variant={ButtonVariant.Primary}
              onClick={submitOrder}
            >
              {getString(`${STRING_PATH}.submitOrder`)}
            </Button>
          </Row>
        </Col>
      </Container>
    </Col>
  );
};
